import React from 'react';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import {
  type ComparisonCell,
  type ComparisonItemId,
  PRICE_COMPARISON_ECHO_LINE,
  PRICE_COMPARISON_FOOTNOTE,
  PRICE_COMPARISON_ITEMS,
  PRICE_COMPARISON_ROWS,
  PRICE_COMPARISON_SOURCES,
  PRICE_COMPARISON_TITLE,
} from '@lingoping/core';
import { Echo, EchoLogo } from '../Echo.js';
import { visuallyHidden } from '../../options/format.js';
import { BRAND } from './brand.js';

/**
 * Echo's price comparison (content: core/priceComparison.ts), drawn like a SaaS plan table:
 * LingoPing is the highlighted "recommended" column and sits first, so on a phone, where the
 * table scrolls sideways under a pinned row-label column, it is the column in view.
 */

const ORDER: readonly ComparisonItemId[] = ['lingoping', 'cappuccino', 'beer', 'cigarettes'];

/** The compact version (lock screen) keeps three rows. */
const COMPACT_ROWS = new Set(['Lasts', 'Your memory', 'Echo says']);

const LABEL_WIDTH = 104;
const sourceId = (index: number) => `price-source-${index + 1}`;

/** Small flat drawings for the three habits; LingoPing wears Echo's icon. */
const Drawing: React.FC<{ id: ComparisonItemId }> = ({ id }) => {
  const common = { width: 44, height: 44, viewBox: '0 0 44 44', 'aria-hidden': true } as const;
  switch (id) {
    case 'cappuccino':
      return (
        <svg {...common}>
          <ellipse cx="22" cy="36" rx="16" ry="3.5" fill="#cbd5e1" />
          <path
            d="M9 14h24v9a12 12 0 0 1-12 12h0A12 12 0 0 1 9 23z"
            fill="#f8fafc"
            stroke="#94a3b8"
            strokeWidth="2"
          />
          <path d="M33 17h2.5a4.5 4.5 0 0 1 0 9H32" fill="none" stroke="#94a3b8" strokeWidth="2" />
          <ellipse cx="21" cy="14" rx="12" ry="3" fill="#a16207" />
          <path d="M15 14c2-2 4 2 6 0s4 2 6 0" fill="none" stroke="#fde68a" strokeWidth="1.6" />
          <path
            d="M16 7c-2 2 2 3 0 5M22 6c-2 2 2 3 0 5"
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'beer':
      return (
        <svg {...common}>
          <rect
            x="9"
            y="14"
            width="22"
            height="24"
            rx="3"
            fill="#fbbf24"
            stroke="#b45309"
            strokeWidth="2"
          />
          <path
            d="M31 19h3.5a3.5 3.5 0 0 1 3.5 3.5v5a3.5 3.5 0 0 1-3.5 3.5H31"
            fill="none"
            stroke="#b45309"
            strokeWidth="2"
          />
          <path
            d="M9 14a4 4 0 0 1 3-6 5 5 0 0 1 8-2 5 5 0 0 1 8 2 4 4 0 0 1 3 6z"
            fill="#fff"
            stroke="#cbd5e1"
            strokeWidth="1.6"
          />
          <path
            d="M15 21v12M21 21v12M27 21v12"
            stroke="#fde68a"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'cigarettes':
      return (
        <svg {...common}>
          <rect
            x="11"
            y="15"
            width="22"
            height="24"
            rx="3"
            fill="#e2e8f0"
            stroke="#64748b"
            strokeWidth="2"
          />
          <rect x="11" y="15" width="22" height="7" rx="2" fill="#dc2626" />
          <rect
            x="15"
            y="25"
            width="14"
            height="9"
            rx="1.5"
            fill="#fff"
            stroke="#94a3b8"
            strokeWidth="1.4"
          />
          <rect
            x="17"
            y="6"
            width="3.6"
            height="10"
            rx="1"
            fill="#fff"
            stroke="#94a3b8"
            strokeWidth="1.4"
          />
          <rect
            x="23"
            y="9"
            width="3.6"
            height="7"
            rx="1"
            fill="#fff"
            stroke="#94a3b8"
            strokeWidth="1.4"
          />
          <path
            d="M18 3c-2 2 2 2 0 4"
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'lingoping':
      return <EchoLogo size={44} />;
  }
};

const TONE_LABEL: Record<ComparisonCell['tone'], string> = {
  good: 'Good: ',
  bad: 'Bad: ',
  neutral: 'Meh: ',
};

const ToneMark: React.FC<{ tone: ComparisonCell['tone'] }> = ({ tone }) => (
  <span style={{ flexShrink: 0, lineHeight: 1, marginTop: 2, fontSize: 17 }} aria-hidden>
    {tone === 'good' && <CheckCircleFilled style={{ color: '#16a34a' }} />}
    {tone === 'bad' && <CloseCircleFilled style={{ color: '#dc2626' }} />}
    {tone === 'neutral' && (
      <svg width="1em" height="1em" viewBox="0 0 16 16" style={{ display: 'block' }}>
        <circle cx="8" cy="8" r="8" fill="#f59e0b" />
        <circle cx="5.4" cy="6.4" r="1.1" fill="#fff" />
        <circle cx="10.6" cy="6.4" r="1.1" fill="#fff" />
        <path d="M4.8 10.8h6.4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )}
  </span>
);

/** Jumps to the source in the list below without touching the address (it holds the tab). */
function jumpToSource(event: React.MouseEvent, index: number) {
  event.preventDefault();
  const target = document.getElementById(sourceId(index));
  target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  target?.focus({ preventScroll: true });
}

interface Props {
  /** The lock screen's shorter version: three rows, no speech bubble. */
  compact?: boolean;
}

export const PriceComparison: React.FC<Props> = ({ compact = false }) => {
  const rows = compact
    ? PRICE_COMPARISON_ROWS.filter((row) => COMPACT_ROWS.has(row.label))
    : PRICE_COMPARISON_ROWS;
  const itemOf = (id: ComparisonItemId) => PRICE_COMPARISON_ITEMS.find((item) => item.id === id)!;
  const usedSources = [
    ...new Set(
      rows.flatMap((row) => ORDER.map((id) => row.cells[id].source)).filter((n) => n !== undefined)
    ),
  ].sort((a, b) => a - b);

  const colWidth = compact ? 128 : 144;
  const cellPad = compact ? '8px 10px' : '12px 12px';
  const fontSize = compact ? 13 : 14;

  return (
    <section aria-labelledby="price-comparison-title" style={{ width: '100%' }}>
      <h2
        id="price-comparison-title"
        style={{
          margin: '0 0 12px',
          textAlign: 'center',
          fontSize: compact ? 20 : 28,
          lineHeight: 1.2,
          color: BRAND.deep,
        }}
      >
        {PRICE_COMPARISON_TITLE}
      </h2>

      {!compact && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            margin: '0 auto 22px',
            maxWidth: 640,
          }}
        >
          <Echo mood="point" size={84} />
          <div
            style={{
              position: 'relative',
              flex: '1 1 0',
              minWidth: 0,
              background: '#fff',
              border: `2px solid ${BRAND.line}`,
              borderRadius: 18,
              padding: '12px 16px',
              fontSize: 16,
              lineHeight: 1.4,
              color: BRAND.deep,
              boxShadow: '0 4px 14px rgba(91, 80, 232, 0.12)',
            }}
          >
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: -9,
                top: '50%',
                width: 14,
                height: 14,
                marginTop: -7,
                background: '#fff',
                borderLeft: `2px solid ${BRAND.line}`,
                borderBottom: `2px solid ${BRAND.line}`,
                transform: 'rotate(45deg)',
              }}
            />
            {PRICE_COMPARISON_ECHO_LINE}
          </div>
        </div>
      )}

      <div
        role="region"
        aria-label="Price comparison table, scrolls sideways on a narrow screen"
        tabIndex={0}
        style={{ overflowX: 'auto', paddingTop: 16, paddingBottom: 6 }}
      >
        <table
          style={{
            width: '100%',
            minWidth: LABEL_WIDTH + colWidth * ORDER.length,
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            fontSize,
            lineHeight: 1.35,
          }}
        >
          <colgroup>
            <col style={{ width: LABEL_WIDTH }} />
            {ORDER.map((id) => (
              <col key={id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <td
                style={{
                  position: 'sticky',
                  left: 0,
                  background: '#f8fafc',
                  zIndex: 2,
                }}
              />
              {ORDER.map((id) => {
                const item = itemOf(id);
                const hero = id === 'lingoping';
                return (
                  <th
                    key={id}
                    scope="col"
                    style={{
                      position: 'relative',
                      padding: compact ? '10px 8px' : '14px 8px',
                      textAlign: 'center',
                      verticalAlign: 'bottom',
                      fontWeight: 600,
                      color: hero ? '#fff' : '#334155',
                      background: hero
                        ? `linear-gradient(160deg, ${BRAND.main}, ${BRAND.dark})`
                        : 'transparent',
                      borderRadius: hero ? '14px 14px 0 0' : 0,
                      boxShadow: hero ? '0 8px 24px rgba(91, 80, 232, 0.35)' : 'none',
                    }}
                  >
                    {hero && (
                      <span
                        style={{
                          position: 'absolute',
                          top: -13,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          whiteSpace: 'nowrap',
                          background: '#fbbf24',
                          color: BRAND.deep,
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          padding: '3px 10px',
                          borderRadius: 999,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                        }}
                      >
                        Echo&apos;s pick
                      </span>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <Drawing id={id} />
                    </div>
                    <div style={{ fontSize: compact ? 14 : 15 }}>{item.name}</div>
                    <div
                      style={{
                        fontSize: compact ? 18 : 22,
                        fontWeight: 800,
                        lineHeight: 1.2,
                        color: hero ? '#fff' : BRAND.deep,
                      }}
                    >
                      {item.price}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: hero ? '#e0defb' : '#64748b',
                      }}
                    >
                      {item.per}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const last = rowIndex === rows.length - 1;
              return (
                <tr key={row.label}>
                  <th
                    scope="row"
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      background: '#f8fafc',
                      padding: cellPad,
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#334155',
                      borderTop: '1px solid #e2e8f0',
                      borderRight: '1px solid #e2e8f0',
                    }}
                  >
                    {row.label}
                  </th>
                  {ORDER.map((id) => {
                    const cell = row.cells[id];
                    const hero = id === 'lingoping';
                    return (
                      <td
                        key={id}
                        style={{
                          padding: cellPad,
                          verticalAlign: 'top',
                          background: hero ? BRAND.tint : '#fff',
                          borderTop: '1px solid #e2e8f0',
                          borderLeft: hero ? `2px solid ${BRAND.main}` : undefined,
                          borderRight: hero ? `2px solid ${BRAND.main}` : undefined,
                          borderBottom: hero && last ? `2px solid ${BRAND.main}` : undefined,
                          borderRadius: hero && last ? '0 0 14px 14px' : 0,
                          fontWeight: hero ? 600 : 400,
                          color: hero ? BRAND.deep : '#334155',
                        }}
                      >
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <ToneMark tone={cell.tone} />
                          <span>
                            <span style={visuallyHidden}>{TONE_LABEL[cell.tone]}</span>
                            {cell.text}
                            {cell.source !== undefined && (
                              <sup style={{ marginLeft: 2 }}>
                                <a
                                  href={`#${sourceId(cell.source)}`}
                                  aria-label={`Source ${cell.source + 1}`}
                                  onClick={(event) => jumpToSource(event, cell.source!)}
                                  style={{ color: BRAND.main, fontWeight: 700 }}
                                >
                                  {cell.source + 1}
                                </a>
                              </sup>
                            )}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p
        style={{
          margin: '10px 0 0',
          textAlign: 'center',
          fontSize: 13,
          color: '#5b6472',
        }}
      >
        {PRICE_COMPARISON_FOOTNOTE}
      </p>
      {usedSources.length > 0 && (
        <ol
          style={{
            margin: '8px auto 0',
            padding: '0 0 0 20px',
            maxWidth: 640,
            fontSize: 12,
            color: '#5b6472',
          }}
        >
          {usedSources.map((index) => {
            const source = PRICE_COMPARISON_SOURCES[index];
            return (
              <li
                key={index}
                id={sourceId(index)}
                tabIndex={-1}
                value={index + 1}
                style={{ outline: 'none' }}
              >
                <a href={source.url} target="_blank" rel="noopener noreferrer">
                  {source.short}
                </a>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};
